import { useEffect, useState } from "preact/hooks";
import QRCode from "qrcode";
import { toast } from "react-hot-toast";
import { apiClient } from "../../constants.ts";
import { Button, CopyButton, Spinner } from "./components.tsx";
import { useAuth } from "./db.ts";

type CountryOption = {
  countryId: string;
  name: string;
  iso: string;
  priceUsd: number;
};

type SmsMessage = {
  id: string;
  fromNumber: string;
  body: string;
  receivedAt: number;
  verificationCode?: string;
};

type WizardStep = "choose_number" | "activate_phone" | "scan_qr" | "connected";

export const DedicatedWhatsappWizard = ({
  botId,
  token,
  isOpen,
  onClose,
  onSuccess,
}: {
  botId: string;
  token: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (phoneNumber: string) => void;
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState<WizardStep>("choose_number");
  const [tab, setTab] = useState<"buy" | "own">("buy");
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [countryLoading, setCountryLoading] = useState(false);
  const [availability, setAvailability] = useState<
    {
      available: number;
      priceUsd: number;
      sampleNumber?: string;
    } | null
  >(null);

  const [ownNumber, setOwnNumber] = useState("");
  const [activeNumber, setActiveNumber] = useState("");
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 2: SMS listener
  const [smsMessages, setSmsMessages] = useState<SmsMessage[]>([]);
  const [latestCode, setLatestCode] = useState<string | null>(null);

  // Step 3: QR / Pairing
  const [preferCode, setPreferCode] = useState(false);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);

  useEffect(() => {
    if (!isOpen || !token || !user?.email) return;
    apiClient({
      endpoint: "list-number-countries",
      token,
      payload: {},
    }).then((res) => {
      if (res.success && res.countries.length > 0) {
        setCountries(res.countries);
        const il = res.countries.find((c) =>
          c.iso === "IL" || c.countryId === "93"
        );
        setSelectedCountryId(il ? il.countryId : res.countries[0].countryId);
      }
    }).catch((err) => {
      console.error("Failed to fetch country list:", err);
    });
  }, [isOpen, token, user?.email]);

  useEffect(() => {
    if (!selectedCountryId || !token || !user?.email) return;
    setCountryLoading(true);
    setError(null);
    apiClient({
      endpoint: "get-number-country-availability",
      token,
      payload: { countryId: selectedCountryId },
    }).then((res) => {
      if (res.success) {
        setAvailability({
          available: res.available,
          priceUsd: res.priceUsd,
          sampleNumber: res.sampleNumber,
        });
      } else {
        setError(res.error);
      }
    }).catch((err) => {
      console.error("Failed to check availability:", err);
    }).finally(() => {
      setCountryLoading(false);
    });
  }, [selectedCountryId, token, user?.email]);

  // Check if returning from checkout
  useEffect(() => {
    if (!isOpen || !token || !botId || !user?.email) return;
    const url = new URL(globalThis.location.href);
    if (url.searchParams.get("checkout") === "success") {
      const countryParam = url.searchParams.get("countryId") || undefined;
      setIsPurchasing(true);
      apiClient({
        endpoint: "buy-dedicated-number",
        token,
        payload: { botId, countryId: countryParam },
      }).then((res) => {
        if (res.success) {
          setActiveNumber(res.phoneNumber);
          setStep("activate_phone");
          toast.success("Dedicated number activated!");
        } else {
          setError(res.error);
        }
      }).catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
      }).finally(() => {
        setIsPurchasing(false);
      });
    }
  }, [isOpen, token, botId]);

  // Handle number purchase or checkout
  const handleBuyNumber = async () => {
    if (!token || !botId) return;
    setIsPurchasing(true);
    setError(null);
    try {
      const buyRes = await apiClient({
        endpoint: "buy-dedicated-number",
        token,
        payload: { botId, countryId: selectedCountryId },
      });

      if (buyRes.success) {
        setActiveNumber(buyRes.phoneNumber);
        setStep("activate_phone");
        toast.success(`Number +${buyRes.phoneNumber} allocated!`);
        return;
      }

      // If capacity required, trigger Stripe checkout session
      if (
        buyRes.error.toLowerCase().includes("capacity") ||
        buyRes.error.toLowerCase().includes("budget") ||
        buyRes.error.toLowerCase().includes("upgrade")
      ) {
        const currentUrl = new URL(globalThis.location.href);
        currentUrl.searchParams.set("checkout", "success");
        if (selectedCountryId) {
          currentUrl.searchParams.set("countryId", selectedCountryId);
        }
        const checkoutRes = await apiClient({
          endpoint: "create-number-checkout-session",
          token,
          payload: {
            botId,
            countryId: selectedCountryId,
            successUrl: currentUrl.toString(),
          },
        });

        if (checkoutRes.success) {
          globalThis.location.href = checkoutRes.url;
          return;
        }
        throw new Error(
          checkoutRes.error || "Failed to create payment session",
        );
      }

      throw new Error(buyRes.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleUseOwnNumber = () => {
    const cleaned = ownNumber.replace(/\D/g, "");
    if (cleaned.length < 8) {
      setError("Please enter a valid phone number with country code.");
      return;
    }
    setError(null);
    setActiveNumber(cleaned);
    setStep("activate_phone");
  };

  // Poll for SMS when in activate_phone step
  useEffect(() => {
    if (step !== "activate_phone" || !activeNumber || !token) return;
    let isCancelled = false;

    const pollSms = () => {
      apiClient({
        endpoint: "get-number-sms",
        token,
        payload: { phoneNumber: activeNumber },
      }).then((res) => {
        if (!isCancelled && res.success) {
          setSmsMessages(res.messages);
          const found = res.messages.find((m) => !!m.verificationCode);
          if (found?.verificationCode) {
            setLatestCode(found.verificationCode);
          }
        }
      }).catch(() => {});
    };

    pollSms();
    const interval = setInterval(pollSms, 3000);
    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [step, activeNumber, token]);

  // Start provisioning when entering scan_qr step
  useEffect(() => {
    if (step !== "scan_qr" || !activeNumber || !token || !botId) return;
    setIsProvisioning(true);
    setError(null);

    apiClient({
      endpoint: "start-whatsapp-provisioning",
      token,
      payload: {
        botId,
        phoneNumber: activeNumber,
        preferCodeLogin: preferCode,
      },
    }).then((res) => {
      if (res.success) {
        if (res.isConnected) {
          setStep("connected");
          onSuccess(activeNumber);
          return;
        }
        if (res.pairingCode) setPairingCode(res.pairingCode);
        if (res.verificationCode) {
          QRCode.toString(res.verificationCode, {
            type: "svg",
            margin: 1,
            width: 220,
          }).then(setQrSvg).catch(console.error);
        }
      } else {
        setError(res.error);
      }
    }).catch((err) => {
      setError(err instanceof Error ? err.message : String(err));
    }).finally(() => {
      setIsProvisioning(false);
    });

    // Poll connection status
    const pollConnection = setInterval(() => {
      apiClient({
        endpoint: "check-whatsapp-connection",
        token,
        payload: { botId, phoneNumber: activeNumber },
      }).then((res) => {
        if (res.success) {
          if (res.isConnected) {
            clearInterval(pollConnection);
            setStep("connected");
            onSuccess(activeNumber);
            toast.success("WhatsApp successfully linked!");
          } else {
            if (res.pairingCode) setPairingCode(res.pairingCode);
            if (res.verificationCode) {
              QRCode.toString(res.verificationCode, {
                type: "svg",
                margin: 1,
                width: 220,
              }).then(setQrSvg).catch(console.error);
            }
          }
        }
      }).catch(() => {});
    }, 2500);

    return () => clearInterval(pollConnection);
  }, [step, activeNumber, preferCode, token, botId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xl bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle rounded-2xl shadow-2xl p-6 sm:p-8 flex flex-col gap-6 text-left my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with step indicators */}
        <div className="flex flex-col gap-2 border-b border-border-subtle-light dark:border-border-subtle pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary font-display flex items-center gap-2">
                <span>💬</span> Make me a WhatsApp Account
              </h2>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary mt-1">
                Set up a dedicated WhatsApp line on your phone and link it to
                your bot.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-text-tertiary-light dark:text-text-tertiary hover:text-text-primary-light dark:hover:text-text-primary text-xl font-bold p-1 cursor-pointer self-start"
            >
              ✕
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary mt-2">
            <span
              className={`px-2.5 py-1 rounded-full ${
                step === "choose_number"
                  ? "bg-accent text-white"
                  : "bg-surface-overlay-light dark:bg-surface-overlay"
              }`}
            >
              1. Number
            </span>
            <span>→</span>
            <span
              className={`px-2.5 py-1 rounded-full ${
                step === "activate_phone"
                  ? "bg-accent text-white"
                  : "bg-surface-overlay-light dark:bg-surface-overlay"
              }`}
            >
              2. Activate
            </span>
            <span>→</span>
            <span
              className={`px-2.5 py-1 rounded-full ${
                step === "scan_qr" || step === "connected"
                  ? "bg-accent text-white"
                  : "bg-surface-overlay-light dark:bg-surface-overlay"
              }`}
            >
              3. Link Bot
            </span>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-600 dark:text-rose-400 text-xs font-medium">
            {error}
          </div>
        )}

        {!user?.email
          ? (
            <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
              <div className="text-4xl">✉️</div>
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary">
                Account Required
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary max-w-sm">
                To connect a dedicated WhatsApp line or purchase a number,
                please sign in or upgrade your guest account with an email.
              </p>
              <a
                href={`/upgrade?redirect_to=${
                  encodeURIComponent(
                    typeof globalThis.location !== "undefined"
                      ? globalThis.location.pathname +
                        globalThis.location.search
                      : "",
                  )
                }`}
                className="mt-2 py-2.5 px-5 rounded-xl font-semibold text-sm bg-accent hover:bg-accent-hover text-white transition-colors cursor-pointer shadow-sm"
              >
                Sign in with Email
              </a>
            </div>
          )
          : (
            <>
              {/* STEP 1: CHOOSE NUMBER */}
              {step === "choose_number" && (
                <div className="space-y-5">
                  {/* PREREQUISITE REQUIREMENT NOTICE */}
                  <div className="p-4 bg-sky-500/10 border border-sky-500/30 rounded-xl flex items-start gap-3 text-xs text-sky-950 dark:text-sky-100">
                    <span className="text-xl leading-none shrink-0 mt-0.5">
                      📱
                    </span>
                    <div className="space-y-1">
                      <span className="font-bold block text-sky-900 dark:text-sky-200">
                        Requires a device with WhatsApp
                      </span>
                      <p className="text-sky-800/90 dark:text-sky-200/80 leading-relaxed">
                        WhatsApp accounts must be created on a phone or tablet.
                        You will receive an SMS code on your number, activate
                        the account in WhatsApp on your device, and then scan a
                        QR code to link your bot.
                      </p>
                    </div>
                  </div>

                  <div className="flex rounded-xl bg-surface-overlay-light dark:bg-surface-overlay p-1 border border-border-subtle-light dark:border-border-subtle">
                    <button
                      type="button"
                      onClick={() => {
                        setTab("buy");
                        setError(null);
                      }}
                      className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                        tab === "buy"
                          ? "bg-surface-light dark:bg-surface text-accent shadow-xs"
                          : "text-text-secondary-light dark:text-text-secondary hover:text-text-primary"
                      }`}
                    >
                      Buy a dedicated number ($10/mo)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTab("own");
                        setError(null);
                      }}
                      className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                        tab === "own"
                          ? "bg-surface-light dark:bg-surface text-accent shadow-xs"
                          : "text-text-secondary-light dark:text-text-secondary hover:text-text-primary"
                      }`}
                    >
                      Use my own number
                    </button>
                  </div>

                  {tab === "buy"
                    ? (
                      <div className="space-y-4">
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary leading-relaxed">
                          We'll provide a dedicated SMS-capable number for your
                          bot. Incoming verification SMS codes will appear
                          directly in this wizard!
                        </p>

                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-text-primary-light dark:text-text-primary">
                            Select country
                          </label>
                          <select
                            value={selectedCountryId}
                            onChange={(e) =>
                              setSelectedCountryId(e.currentTarget.value)}
                            className="w-full px-3 py-2 text-sm bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-xl text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer"
                          >
                            {countries.map((c) => (
                              <option key={c.countryId} value={c.countryId}>
                                {c.name} {c.iso ? `(${c.iso})` : ""}
                              </option>
                            ))}
                          </select>
                        </div>

                        {countryLoading
                          ? (
                            <div className="flex items-center gap-2 text-xs text-text-tertiary-light dark:text-text-tertiary py-2">
                              <Spinner className="w-4 h-4 text-accent" />
                              <span>Checking number availability...</span>
                            </div>
                          )
                          : availability
                          ? (
                            <div className="p-4 bg-accent/5 border border-accent/20 rounded-xl space-y-1.5">
                              <div className="flex items-center justify-between text-xs font-medium">
                                <span className="text-text-secondary-light dark:text-text-secondary">
                                  Availability:
                                </span>
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                  {availability.available > 0
                                    ? `${availability.available} numbers available`
                                    : "Out of stock"}
                                </span>
                              </div>
                              {availability.sampleNumber && (
                                <div className="flex items-center justify-between text-xs font-medium">
                                  <span className="text-text-secondary-light dark:text-text-secondary">
                                    Sample format:
                                  </span>
                                  <span className="font-mono text-text-primary-light dark:text-text-primary">
                                    +{availability.sampleNumber}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center justify-between text-xs font-medium">
                                <span className="text-text-secondary-light dark:text-text-secondary">
                                  Subscription:
                                </span>
                                <span className="font-bold text-accent">
                                  $10/month
                                </span>
                              </div>
                            </div>
                          )
                          : null}

                        <Button
                          onClick={handleBuyNumber}
                          disabled={isPurchasing || countryLoading ||
                            availability?.available === 0}
                          className="w-full justify-center py-3 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md cursor-pointer"
                        >
                          {isPurchasing
                            ? (
                              <span className="flex items-center gap-2">
                                <Spinner className="w-4 h-4 text-white" />{" "}
                                Allocating Number...
                              </span>
                            )
                            : (
                              "Get Dedicated Number ($10/mo) →"
                            )}
                        </Button>
                      </div>
                    )
                    : (
                      <div className="space-y-4">
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary leading-relaxed">
                          Have a spare SIM card or virtual number? Enter it in
                          full international format (e.g.{" "}
                          <code>+972501234567</code>).
                        </p>

                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-text-primary-light dark:text-text-primary">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={ownNumber}
                            onInput={(e) => setOwnNumber(e.currentTarget.value)}
                            placeholder="+972 50 123 4567"
                            className="w-full px-3 py-2 text-sm bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-xl text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent font-mono"
                          />
                        </div>

                        <Button
                          onClick={handleUseOwnNumber}
                          disabled={!ownNumber.trim()}
                          className="w-full justify-center py-3 text-sm font-semibold bg-accent hover:bg-accent-hover text-white shadow-md cursor-pointer"
                        >
                          Continue with this number →
                        </Button>
                      </div>
                    )}
                </div>
              )}

              {/* STEP 2: ACTIVATE ON PHONE */}
              {step === "activate_phone" && (
                <div className="space-y-5">
                  <div className="flex items-center justify-between p-3.5 bg-surface-overlay-light dark:bg-surface-overlay rounded-xl border border-border-subtle-light dark:border-border-subtle">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary block">
                        Your Dedicated Phone Number
                      </span>
                      <span className="text-base font-bold font-mono text-text-primary-light dark:text-text-primary">
                        +{activeNumber}
                      </span>
                    </div>
                    <CopyButton text={`+${activeNumber}`} />
                  </div>

                  {/* Step by step guide */}
                  <div className="space-y-2 text-xs text-text-secondary-light dark:text-text-secondary">
                    <p>
                      <strong>1.</strong> Open WhatsApp on your device.
                    </p>
                    <p>
                      <strong>2.</strong> Select country and enter:{" "}
                      <code className="bg-surface-overlay px-1.5 py-0.5 rounded font-mono">
                        +{activeNumber}
                      </code>.
                    </p>
                    <p>
                      <strong>3.</strong>{" "}
                      WhatsApp will send a 6-digit verification code.
                    </p>
                  </div>

                  {/* REAL-TIME SMS LISTENER */}
                  <div className="p-4 rounded-xl border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs font-semibold text-emerald-900 dark:text-emerald-200">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75">
                          </span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500">
                          </span>
                        </span>
                        <span>Incoming SMS Listener</span>
                      </div>
                      <span className="text-[10px] text-emerald-800/70 dark:text-emerald-300/70">
                        Auto-updating
                      </span>
                    </div>

                    {latestCode
                      ? (
                        <div className="flex flex-col items-center gap-2 p-3 bg-white dark:bg-surface rounded-xl border border-emerald-300 dark:border-emerald-700 shadow-xs">
                          <span className="text-xs font-medium text-text-secondary-light dark:text-text-secondary">
                            Your WhatsApp Code:
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl font-bold font-mono tracking-widest text-emerald-700 dark:text-emerald-300">
                              {latestCode}
                            </span>
                            <CopyButton text={latestCode.replace(/\D/g, "")} />
                          </div>
                        </div>
                      )
                      : (
                        <div className="text-center py-3 text-xs text-emerald-800/80 dark:text-emerald-200/80 italic">
                          Waiting for WhatsApp to send SMS... Enter the number
                          on your phone to trigger it.
                        </div>
                      )}

                    {smsMessages.length > 0 && (
                      <div className="space-y-1.5 pt-2 border-t border-emerald-200/60 dark:border-emerald-800/60">
                        {smsMessages.map((m) => (
                          <div
                            key={m.id}
                            className="text-[11px] text-emerald-900/90 dark:text-emerald-100/90 bg-white/60 dark:bg-surface/60 p-2 rounded-lg font-mono"
                          >
                            {m.body}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => setStep("scan_qr")}
                    className="w-full justify-center py-3 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md cursor-pointer"
                  >
                    I activated WhatsApp on my phone → Next: Link Bot
                  </Button>
                </div>
              )}

              {/* STEP 3: SCAN QR / LINK BOT */}
              {step === "scan_qr" && (
                <div className="space-y-5 text-center">
                  <div className="text-left space-y-1.5 text-xs text-text-secondary-light dark:text-text-secondary">
                    <p>
                      <strong>1.</strong> In WhatsApp on your device, open{" "}
                      <strong>Settings</strong> (iOS) or <strong>⋮ Menu</strong>
                      {" "}
                      (Android).
                    </p>
                    <p>
                      <strong>2.</strong> Tap <strong>Linked Devices</strong> →
                      {" "}
                      <strong>Link a Device</strong>.
                    </p>
                    <p>
                      <strong>3.</strong> {preferCode
                        ? "Enter this pairing code on your phone:"
                        : "Scan this QR code with your phone camera:"}
                    </p>
                  </div>

                  <div className="flex flex-col items-center justify-center min-h-[240px] p-6 bg-white dark:bg-surface rounded-2xl border border-border-subtle-light dark:border-border-subtle shadow-xs">
                    {isProvisioning
                      ? (
                        <div className="flex flex-col items-center gap-3 py-8">
                          <Spinner className="w-8 h-8 text-accent" />
                          <span className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
                            Initializing cloud session for +{activeNumber}...
                          </span>
                        </div>
                      )
                      : preferCode
                      ? (
                        pairingCode
                          ? (
                            <div className="flex flex-col items-center gap-3">
                              <span className="text-xs font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary">
                                Enter Pairing Code:
                              </span>
                              <div className="text-3xl font-bold font-mono tracking-wider bg-surface-overlay-light dark:bg-surface-overlay px-6 py-3 rounded-xl border border-border-default-light dark:border-border-default text-text-primary-light dark:text-text-primary">
                                {pairingCode}
                              </div>
                              <CopyButton text={pairingCode} />
                            </div>
                          )
                          : (
                            <div className="text-xs text-text-secondary">
                              Requesting pairing code...
                            </div>
                          )
                      )
                      : qrSvg
                      ? (
                        <div className="flex flex-col items-center gap-2">
                          <div
                            className="w-[220px] h-[220px] flex items-center justify-center"
                            dangerouslySetInnerHTML={{ __html: qrSvg }}
                          />
                          <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                            Scan with WhatsApp camera
                          </span>
                        </div>
                      )
                      : (
                        <div className="flex flex-col items-center gap-2 py-8">
                          <Spinner className="w-6 h-6 text-accent" />
                          <span className="text-xs text-text-secondary">
                            Generating QR code...
                          </span>
                        </div>
                      )}
                  </div>

                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => setPreferCode(!preferCode)}
                      className="text-xs text-accent underline font-medium cursor-pointer hover:opacity-80"
                    >
                      {preferCode
                        ? "Switch to QR code scan"
                        : "On the same phone? Get an 8-character Pairing Code instead"}
                    </button>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-xs text-text-tertiary-light dark:text-text-tertiary pt-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75">
                      </span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500">
                      </span>
                    </span>
                    <span>
                      Waiting for connection... Will automatically advance when
                      linked.
                    </span>
                  </div>
                </div>
              )}

              {/* STEP 4: CONNECTED / SUCCESS */}
              {step === "connected" && (
                <div className="space-y-6 text-center py-4">
                  <div className="w-16 h-16 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-3xl mx-auto border border-emerald-500/30">
                    ✓
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-text-primary-light dark:text-text-primary font-display">
                      Bot is live on WhatsApp!
                    </h3>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary max-w-md mx-auto leading-relaxed">
                      Your dedicated number <strong>+{activeNumber}</strong>
                      {" "}
                      is connected and ready to respond to messages.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                    <a
                      href={`https://wa.me/${activeNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-colors cursor-pointer shadow-md inline-flex items-center justify-center gap-2"
                    >
                      <span>Open WhatsApp Chat</span>
                      <span>↗</span>
                    </a>
                    <Button
                      type="button"
                      onClick={onClose}
                      className="w-full sm:w-auto px-6 py-3 rounded-xl text-sm"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
      </div>
    </div>
  );
};
